/**
 * PLAN-LEVEL integration gating — the single answer to "is this integration
 * available to this organization?", shared by the Integrations page grid
 * (`pages/Integrations.tsx`) and the sidebar's Integrations nav entry
 * (`components/layout/AppSidebar.tsx`).
 *
 * WHY THIS FILE EXISTS: those two surfaces each carried their own copy of the
 * rule and drifted, which is the bug class this module closes. The page decided
 * per card, the sidebar decided "is anything available", and neither knew about
 * the other's special cases — so an org could be shown a nav entry that opened a
 * "nothing here" page, or (the reported bug) have a granted integration whose
 * nav entry was hidden AND whose card was filtered out.
 *
 * This module answers ONLY the plan question. Two other gates stack on top and
 * are deliberately NOT here:
 *   - per-user permissions → `isIntegrationGrantedByPermissions` (integrationPermissions.ts)
 *   - route access          → `<FeatureRoute>` / `<IntegrationRoute>` in App.tsx
 *
 * ── STORAGE SHAPES ──────────────────────────────────────────────────────────
 * Most integrations are granted at `sub_features.integrations.<key>` with the
 * cap convention documented in socialAccountCap.ts (0 = off, -1 = unlimited,
 * n ≥ 1 = cap). A few are granted at TOP-LEVEL `features.<key>` instead — see
 * TOP_LEVEL_INTEGRATION_ENTITLEMENT_KEYS below.
 */

import { normalizeSocialCapValue } from './socialAccountCap';

/**
 * Integrations whose grant lives at top-level `features.<key>`, NOT at
 * `sub_features.integrations.<key>`.
 *
 * 🔴 `petpooja` is the only member, and its key must NOT be migrated to the
 * sub-feature shape. Nothing in the backend reads or mirrors
 * `sub_features.integrations.petpooja`: `EntitlementService._syncLegacyMenuFeatureKeys`
 * mirrors instagram / tiktok_ads / tiktok_content / linkedin / facebook / youtube
 * onto legacy top-level keys and pointedly does not include petpooja, and the
 * admin plan + org-override forms both persist `features.petpooja` /
 * `limits.petpooja`. Moving the key would silently revoke PetPooja from every
 * org whose plan grants it today. The admin editor renders the row visually
 * under the Integrations group while still writing the top-level key — see
 * PlanFeaturesTable.tsx.
 *
 * The practical consequence, and the reason this list is shared rather than
 * hardcoded twice: a top-level grant is INDEPENDENT of the parent `integrations`
 * feature, so it must survive the parent being switched off. Everything else
 * stays strictly gated behind the parent.
 */
export const TOP_LEVEL_INTEGRATION_ENTITLEMENT_KEYS: readonly string[] = ['petpooja'];

export const isTopLevelIntegrationEntitlementKey = (key?: string | null): boolean =>
  !!key && TOP_LEVEL_INTEGRATION_ENTITLEMENT_KEYS.includes(key);

/** Loosely typed on purpose — resolved entitlements are JSON from the API. */
type UnknownMap = Record<string, unknown> | null | undefined;

export interface IntegrationEntitlementContext {
  /**
   * Have entitlements actually landed? `false` while loading and on fetch
   * error, which is the LENIENT state: both surfaces show rather than hide, so
   * a transient 401 / network blip never strips a paying org's integrations
   * (and never flickers the nav entry out and back).
   */
  hasEntitlements: boolean;
  /** `entitlements.entitlements.features` */
  features: UnknownMap;
  /** `entitlements.entitlements.sub_features.integrations` */
  subFeatures: UnknownMap;
  /** `entitlements.entitlements.limits` */
  limits?: UnknownMap;
}

/** A cap value that grants access: -1 (unlimited) or any positive cap. */
const capGrants = (raw: unknown): boolean => {
  const cap = normalizeSocialCapValue(raw);
  if (cap === undefined) return false;
  return cap === -1 || cap > 0;
};

/**
 * Is `entitlementKey` available on this org's plan?
 *
 * Resolution order:
 *   1. Entitlements not loaded → allow (lenient, see `hasEntitlements`).
 *   2. Top-level-granted key (petpooja) explicitly `true` → ALLOW even when the
 *      parent `integrations` feature is off. This is the fix for the reported
 *      bug: the grant is what the admin ticked, so the parent toggle must not
 *      silently veto it.
 *   3. Parent `features.integrations !== true` → DENY. Every other integration
 *      remains gated by the parent exactly as before — step 2 is not a general
 *      bypass.
 *   4. `sub_features.integrations[key]` defined → its cap decides.
 *   5. Fallback for keys with no sub-feature entry: `features[key] === false`
 *      denies, a `limits[key]` decides if present, otherwise ALLOW. This
 *      default-allow is load-bearing back-compat — a plan that predates a key
 *      keeps working — and it is why petpooja is visible today whenever the
 *      parent is on but the plan says nothing about it.
 *
 * Cap reading goes through `normalizeSocialCapValue` rather than a raw
 * `=== -1 || > 0` comparison because the stored value is not reliably a number:
 * per-org overrides are VARCHAR (numeric strings routine), the
 * no-subscription branch stores booleans, and legacy plan rows store
 * `{ enabled, limit }` objects. The raw comparison read a stored `"-1"` and a
 * `{ enabled: true, limit: 1 }` as DENIED.
 */
export function isIntegrationEntitled(
  ctx: IntegrationEntitlementContext,
  entitlementKey?: string,
): boolean {
  const { hasEntitlements, features, subFeatures, limits } = ctx;

  if (!hasEntitlements) return true;

  // (2) An explicit top-level grant outlives a disabled parent.
  if (isTopLevelIntegrationEntitlementKey(entitlementKey) && features?.[entitlementKey!] === true) {
    return true;
  }

  // (3) Parent gate — unchanged for every sub-feature integration.
  if (features?.integrations !== true) return false;

  // A card with no entitlement key at all is ungated beyond the parent.
  if (!entitlementKey) return true;

  // (4) Per-integration cap.
  const rawSub = subFeatures?.[entitlementKey];
  if (rawSub !== undefined && rawSub !== null) {
    const cap = normalizeSocialCapValue(rawSub);
    // An unparseable value is treated as "the plan said nothing" and falls
    // through, rather than denying on a shape we failed to read.
    if (cap !== undefined) return cap === -1 || cap > 0;
  }

  // (5) Top-level fallback (petpooja and any future standalone key).
  if (features?.[entitlementKey] === false) return false;
  const rawLimit = limits?.[entitlementKey];
  if (rawLimit !== undefined && rawLimit !== null) {
    const limit = normalizeSocialCapValue(rawLimit);
    if (limit !== undefined) return limit === -1 || limit > 0;
  }
  return true;
}

/**
 * Does this org have at least ONE usable integration? Drives whether the
 * sidebar's Integrations nav entry renders at all — an entry that only ever
 * opens a "nothing here" page is worse than no entry.
 *
 * Deliberately built ON TOP of `isIntegrationEntitled` for the top-level keys
 * instead of re-deriving their rule, so the nav entry and the card can never
 * disagree about petpooja: the same predicate covers both the explicit
 * parent-off grant AND the parent-on default-allow.
 */
export function hasAnyEntitledIntegration(ctx: IntegrationEntitlementContext): boolean {
  const { hasEntitlements, features, subFeatures } = ctx;

  if (!hasEntitlements) return true;

  // Any top-level-granted integration keeps the entry — including the
  // parent-off case, which is exactly the reported bug.
  if (TOP_LEVEL_INTEGRATION_ENTITLEMENT_KEYS.some((key) => isIntegrationEntitled(ctx, key))) {
    return true;
  }

  if (features?.integrations !== true) return false;

  // Parent granted but no per-integration caps defined → legacy plan shape,
  // everything is implicitly allowed. Hiding these would revoke access from
  // orgs that have it today.
  if (!subFeatures) return true;

  return Object.values(subFeatures).some(capGrants);
}
